import * as React from 'react';

import type { NavigationScreenModifier, NavigationScreenSpaceState } from '@gitbook/app-navigation';
import type { Revision } from '@gitbook/hive-core';
import * as hiveCore from '@gitbook/hive-core';

import { NavigationRedirect, useNavigation } from '../../navigation';

/**
 * Hook to navigate to other pages in a space.
 */
export function useContentNavigation(): {
    getNavigationHref: GetNavigationHrefFn;
    getNavigationProps: GetNavigationProps;
    navigateToPagePath: NavigateToPagePathFn;
    navigateToPage: NavigateToPageFn;
} {
    //
    // Navigate to a specific page in this space.
    //
    const { navigate, getNavigationURL, getNavigationLinkProps } = useNavigation();

    //
    // Value for context to navigate in content
    //
    const navigateContent = React.useMemo(() => {
        const getNavigationHref: GetNavigationHrefFn = (revision: Revision, pageId: string) => {
            const path = hiveCore.getAbsolutePagePathInRevision(revision, pageId);
            if (path === null) {
                return;
            }

            return getNavigationURL({
                current: getNavigationScreenForPage(path),
            });
        };

        const getNavigationProps: GetNavigationProps = (revision: Revision, pageId: string) => {
            const path = hiveCore.getAbsolutePagePathInRevision(revision, pageId);
            if (path === null) {
                return;
            }

            return getNavigationLinkProps({
                current: getNavigationScreenForPage(path),
            });
        };

        const navigateToPagePath: NavigateToPagePathFn = (page, options = {}) => {
            const { replace = false } = options;
            navigate(
                {
                    current: getNavigationScreenForPage(page, options),
                },
                {
                    replace,
                }
            );
        };

        const navigateToPage: NavigateToPageFn = (revision, pageId, options) => {
            const path = hiveCore.getAbsolutePagePathInRevision(revision, pageId);
            if (path === null) {
                return;
            }

            navigateToPagePath(path, options);
        };

        return {
            getNavigationHref,
            getNavigationProps,
            navigateToPagePath,
            navigateToPage,
        };
    }, [navigate]);

    return navigateContent;
}

/**
 * Get the screen to navigate to a page in a space.
 */
function getNavigationScreenForPage(
    pagePath: string,
    options: NavigateToPageOptions = {}
): NavigationScreenModifier {
    const { preserveSideSheet = false, preserveComments = true, sideSheet } = options;

    // @ts-expect-error
    return (prev) => {
        if (prev.type !== 'space') {
            return prev;
        }

        return {
            page: pagePath,
            ...(!preserveSideSheet
                ? { sideSheet: undefined } // If we are not preserving the side sheet, we need to reset it.
                : 'sideSheet' in prev
                  ? {
                        // Otherwise we need to preserve the side sheet
                        sideSheet: {
                            // If while preserving the side sheet, we are not preserving the comments, we reset the comments side sheet.
                            ...(prev.sideSheet?.type === 'comments' && !preserveComments
                                ? { type: 'comments' }
                                : prev.sideSheet), // Otherwise we preserve the side sheet as is.
                        },
                    }
                  : {}),
            ...(sideSheet ? { sideSheet } : {}),
        };
    };
}

interface NavigateToPageOptions {
    replace?: boolean;

    /**
     * If true, keep the current side sheet opened.
     * @default false
     */
    preserveSideSheet?: boolean;

    /**
     * If true, keep the comments opened.
     * @default false
     */
    preserveComments?: boolean;

    /**
     * Side-sheet to open for the page.
     */
    sideSheet?: NavigationScreenSpaceState['sideSheet'];
}

export type GetNavigationHrefFn = (revision: Revision, pageId: string) => string | undefined;
export type GetNavigationProps = (revision: Revision, pageId: string) => any;
export type NavigateToPageFn = (
    revision: Revision,
    pageId: string,
    options?: NavigateToPageOptions
) => void;
export type NavigateToPagePathFn = (pagePath: string, options?: NavigateToPageOptions) => void;

/**
 * Component for declarative redirect to a page in a space. Also compatible with SSR.
 */
export function NavigateToSpacePath(props: { to: string }): React.ReactElement | null {
    const { to } = props;

    return (
        <NavigationRedirect
            to={{
                screen: {
                    page: to,
                    sideSheet: undefined,
                },
            }}
        />
    );
}
